/**
 * PasswordField - Password input with show/hide toggle
 *
 * Use with React Hook Form:
 * <PasswordField label="Password" error={errors.password?.message} {...register('password')} />
 */

import { Eye, EyeOff, Lock } from 'lucide-react';
import { forwardRef, useId, useState } from 'react';

import { cn } from '../utils';

export interface PasswordFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Field label */
  label: string;
  /** Error message from form validation */
  error?: string;
  /** Optional helper text shown below the input */
  hint?: string;
  /** Whether to show the lock icon (default: true) */
  showLockIcon?: boolean;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ label, error, hint, showLockIcon = true, className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = useId();

    return (
      <div className={className}>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
        >
          {label}
        </label>
        <div className="relative mt-1">
          {showLockIcon && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400 dark:text-zinc-500">
              <Lock className="h-4 w-4" />
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? 'text' : 'password'}
            className={cn(
              'w-full rounded-lg border py-2.5 pr-10 text-sm transition-all',
              'bg-gray-50 text-gray-900 placeholder-gray-400',
              'dark:bg-[#18181b] dark:text-white dark:placeholder-zinc-500',
              'focus:outline-none focus:ring-1',
              showLockIcon ? 'pl-10' : 'pl-4',
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-white/[0.06]',
              props.disabled && 'cursor-not-allowed opacity-50'
            )}
            {...props}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {hint && !error && <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
      </div>
    );
  }
);

PasswordField.displayName = 'PasswordField';
